import { useEffect, useRef, useState } from "react";
import { X, Send, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { fetchQuestionThread, replyToQuestion } from "../lib/api";
import { MESSAGES } from "../lib/testIds";

const formatWhen = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const now = new Date();
        const same =
            d.getFullYear() === now.getFullYear() &&
            d.getMonth() === now.getMonth() &&
            d.getDate() === now.getDate();
        if (same) {
            return d.toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "2-digit",
            });
        }
        return d.toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
};

const QuestionThreadModal = ({ questionId, open, onClose, onChange }) => {
    const [thread, setThread] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState("");
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (!open || !questionId) return;
        let cancelled = false;
        setLoading(true);
        fetchQuestionThread(questionId)
            .then((data) => {
                if (cancelled) return;
                setThread(data);
                onChange?.();
            })
            .catch(() => {
                if (!cancelled) toast.error("Couldn't load conversation");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, questionId, onChange]);

    useEffect(() => {
        // Auto-scroll to newest message after render.
        if (!thread) return;
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [thread?.messages?.length]);

    if (!open) return null;

    const handleSend = async () => {
        const trimmed = reply.trim();
        if (!trimmed) return;
        try {
            setSending(true);
            const { message } = await replyToQuestion(questionId, trimmed);
            setThread((prev) =>
                prev
                    ? { ...prev, messages: [...(prev.messages || []), message] }
                    : prev,
            );
            setReply("");
            onChange?.();
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                err?.message ||
                "Try again in a moment.";
            toast.error("Couldn't send reply", {
                description: typeof detail === "string" ? detail : "Server error",
            });
        } finally {
            setSending(false);
        }
    };

    const otherName =
        thread?.your_role === "owner"
            ? thread?.asker_name
            : thread?.owner_name;

    return (
        <div
            data-testid={MESSAGES.threadModal}
            className="fixed inset-0 z-[70] flex items-end justify-center bg-[#2e3220]/50 backdrop-blur-sm sm:items-center"
            onClick={onClose}
        >
            <div
                className="relative flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-[#F4F1EA] shadow-2xl sm:rounded-3xl"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-start gap-3 border-b border-[#2e3220]/8 bg-[#F4F1EA] px-5 py-4">
                    {thread?.item_image ? (
                        <img
                            src={thread.item_image}
                            alt={thread.item_name}
                            className="h-11 w-11 shrink-0 rounded-xl object-cover"
                        />
                    ) : (
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#737a3d]/12 text-[#737a3d]">
                            <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
                        </span>
                    )}
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            {thread?.item_name || "Conversation"}
                        </p>
                        <p className="truncate font-display text-lg font-bold leading-tight text-[#2e3220]">
                            {thread?.subject || "Question"}
                        </p>
                        {otherName && (
                            <p className="mt-0.5 truncate text-xs text-[#5a6142]">
                                with {otherName}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        data-testid={MESSAGES.threadClose}
                        onClick={onClose}
                        aria-label="Close conversation"
                        className="rounded-full p-1.5 text-[#5a6142] hover:bg-[#2e3220]/5"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </header>

                <div
                    ref={scrollRef}
                    className="flex-1 space-y-3 overflow-y-auto px-5 py-5"
                >
                    {loading ? (
                        <div className="flex items-center justify-center py-10">
                            <Loader2 className="h-5 w-5 animate-spin text-[#737a3d]" />
                        </div>
                    ) : thread?.messages?.length ? (
                        thread.messages.map((m) => {
                            const mine = m.sender_role === thread.your_role;
                            return (
                                <div
                                    key={m.id}
                                    data-testid={MESSAGES.message(m.id)}
                                    className={`flex ${mine ? "justify-end" : "justify-start"}`}
                                >
                                    <div
                                        className={`max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                                            mine
                                                ? "bg-[#737a3d] text-[#F4F1EA]"
                                                : "bg-white text-[#2e3220] shadow-sm"
                                        }`}
                                    >
                                        <p
                                            className={`text-[10px] font-bold uppercase tracking-widest ${mine ? "text-[#F4F1EA]/70" : "text-[#737a3d]"}`}
                                        >
                                            {mine ? "You" : m.sender_name || "Neighbor"} ·{" "}
                                            {formatWhen(m.created_at)}
                                        </p>
                                        <p className="mt-1 whitespace-pre-wrap break-words">
                                            {m.body}
                                        </p>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <p className="py-10 text-center text-sm text-[#5a6142]">
                            No messages yet.
                        </p>
                    )}
                </div>

                <footer className="border-t border-[#2e3220]/8 bg-[#F4F1EA] px-5 py-4">
                    <div className="flex items-end gap-2">
                        <textarea
                            value={reply}
                            onChange={(e) => setReply(e.target.value.slice(0, 2000))}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                            rows={2}
                            data-testid={MESSAGES.replyInput}
                            placeholder="Write a reply…"
                            className="w-full resize-none rounded-2xl border border-[#2e3220]/15 bg-white/70 px-4 py-2.5 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                        />
                        <button
                            type="button"
                            data-testid={MESSAGES.replySend}
                            onClick={handleSend}
                            disabled={sending || !reply.trim()}
                            className="btn-primary self-stretch disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {sending ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Send className="h-4 w-4" strokeWidth={2.5} />
                            )}
                        </button>
                    </div>
                    <p className="mt-1.5 text-[10px] text-[#5a6142]/70">
                        {reply.length}/2000 · ⌘/Ctrl + Enter to send · Both replies
                        also arrive by email.
                    </p>
                </footer>
            </div>
        </div>
    );
};

export default QuestionThreadModal;
